import { useQueries, useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useCampaignMutation } from "./useCampaignMutation";
import type { ContentItem, ContentPlacement } from "../../lib/types";

export interface ContentDraft {
	enabled: boolean;
	settings?: Record<string, unknown>;
	title: string;
	altText: string | null;
	desktopAssetId: string | null;
	mobileAssetId: string | null;
	startsAt: string | null;
	endsAt: string | null;
}

const key = (campaignId: string, placement: ContentPlacement) => ["content", campaignId, placement];

export const useContent = (campaignId: string, placement: ContentPlacement) =>
	useQuery({
		queryKey: key(campaignId, placement),
		queryFn: () =>
			api.get<{ items: ContentItem[] }>(`/api/campaigns/${campaignId}/content/${placement}`),
	});

/**
 * One query per placement. The library shows every content type at once, and a
 * fixed list of hooks cannot express a registry that grows.
 */
export const useContentByPlacement = (
	campaignId: string,
	placements: readonly ContentPlacement[],
) =>
	useQueries({
		queries: placements.map((placement) => ({
			queryKey: key(campaignId, placement),
			queryFn: () =>
				api.get<{ items: ContentItem[] }>(`/api/campaigns/${campaignId}/content/${placement}`),
		})),
	});

const useContentMutation = <TVariables>(
	campaignId: string,
	placement: ContentPlacement,
	run: (variables: TVariables) => Promise<unknown>,
) => useCampaignMutation(campaignId, [key(campaignId, placement)], run);

export const useCreateContent = (campaignId: string, placement: ContentPlacement) =>
	useContentMutation(campaignId, placement, (draft: ContentDraft) =>
		api.post(`/api/campaigns/${campaignId}/content/${placement}`, draft),
	);

export const useUpdateContent = (campaignId: string, placement: ContentPlacement) =>
	useContentMutation(
		campaignId,
		placement,
		({ itemId, ...draft }: { itemId: string } & Partial<ContentDraft>) =>
			api.put(`/api/campaigns/${campaignId}/content/items/${itemId}`, draft),
	);

export const useDeleteContent = (campaignId: string, placement: ContentPlacement) =>
	useContentMutation(campaignId, placement, (itemId: string) =>
		api.remove(`/api/campaigns/${campaignId}/content/items/${itemId}`),
	);
